import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import TitleBar from "../../common/title-bar/title-bar";
import Table from "../../common/table";
import TableOptionsCell from "../../common/table/table-options-cell";
import Button from "../../common/button/button";
import DropdownComponent from "../../common/dropdown-input";
import APIService from "../../Service/APIService";
import { useNavigate } from "react-router-dom";
import TableLink from "../../common/table/table-link";
import moment from "moment";
import { useAuth } from '../AuthProvider.js';
import Swal from "sweetalert2";
import { colors } from "../../theme/colors";
import { exportToCSV, exportToXLSX } from "../Utils/ExcelExports";
import { hasPermission } from "../../Config/permissions.js";

const COMMISSION_EXPORT_COLUMNS = [
  { key: "TissueID", header: "Tissue ID" },
  { key: "ProductCode", header: "ProductCode" },
  {
    key: "Cost",
    header: "Cost",
    transform: (value) =>
      Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  },
  {
    key: "Paid",
    header: "Paid",
    transform: (value) =>
      Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  },
  {
    key: "CommissionRate",
    header: "Comm. Rate",
    transform: (value) => {
      const normalizedValue = Number(value);
      return Number.isFinite(normalizedValue) ? `${normalizedValue}%` : "";
    },
  },
  {
    key: "CommissionDue",
    header: "Comm. Due",
    transform: (value) =>
      Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  },
  {
    key: "DateApplied",
    header: "Date Applied",
    transform: (value) => (value ? moment(value).format("MM-DD-YYYY") : ""),
  },
  { key: "InvoiceNumber", header: "InvoiceNumber" },
  { key: "PONumber", header: "PONumber" },
  {
    key: "InvoiceDate",
    header: "InvoiceDate",
    transform: (value) => (value ? moment(value).format("MM-DD-YYYY") : ""),
  },
  { key: "AccountName", header: "AccountName" },
  { key: "Manufacturer", header: "Manufacturer" },
  {
    key: "OpenBalance",
    header: "Open Balance",
    transform: (value) =>
      Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  },
];

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const normalizedValue = Number(
    String(value)
      .replace(/,/g, "")
      .replace(/[^0-9.-]+/g, "")
  );

  return Number.isFinite(normalizedValue) ? normalizedValue : 0;
};

const CommissionStatementsDistributor = () => {
  const { user } = useAuth();
  const userData = user;
  const { id } = useParams();
  const [commissionData, setCommissionData] = useState([]);
  const [referralCommissionData, setReferralCommissionData] = useState([]);
  const navigate = useNavigate();
  const [selectedSegment, setSelectedSegment] = useState("Sales");
  const location = useLocation();
  const [isStatementGenerated, setIsStatementGenerated] = useState(
    typeof location.state?.IsGenerated === "boolean"
      ? location.state.IsGenerated
      : null
  );
  const [exportOption, setExportOption] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const distributorID = location.state?.DistributorID || id;
  const commissionStatementID = location.state?.CommissionStatementID || null;

  const handleSegmentChange = (value) => {
    setSelectedSegment(value);
  };

  const getStatementPayload = () => ({
    DistributorID: distributorID,
    CommissionStatementID: commissionStatementID,
  });

  const getReferralCommissionData = async () => {
    const apiServe = new APIService();
    const response = await apiServe.addDataToBackend(
      getStatementPayload(),
      "GetReferralCommissionsByDistID"
    );
    console.log("getReferralCommissionData", response.data.data);
    if (response) {
      return response?.data?.data || [];
    }
    return [];
  }

  const getData = async () => {
    const apiServe = new APIService();
    const response = await apiServe.addDataToBackend(
      getStatementPayload(),
      "GetInvoiceBasedCommissionsByDistID"
    );
    console.log("getData", response.data.data);
    if (response) {
      return response?.data?.data || [];
    }
    return [];
  };
  const fetchDist = async (distID) =>{
    const apiServe = new APIService();
    const response = await apiServe.getDataFromBackend("getdistributorbyid?ID="+distID);
    return response?.data?.data;
  }

  const [statementData, setStatementData] = useState(null);

  const fetchStatementGenerated = async (statementID) => {
    try {
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(
        `getcommissionsbystatementmonth?ID=${statementID}`
      );
      setStatementData(response?.data?.Statement || null);
      return Boolean(response?.data?.Statement?.IsGenerated);
    } catch (error) {
      console.error("Failed to fetch commission statement status", error);
      return null;
    }
  };

  useEffect(() => {
    // const stateData = location.state ?? (location.state.data || {});
    console.log("State Data:", location.state);
    if (userData && userData.Type === "Account") {
      navigate(`/Account/${userData.AccountID}`);
      window.location.reload();
    } else if (userData && userData.Type === "Distributor") {
      fetchDist(userData.DistributorID).then((data)=>{
        if(!userData.IsManager || !data.EnableCommissionModule){
          navigate(`/Distributor/${userData.DistributorID}`);
          window.location.reload();
        }
      });
    } else if (
      userData &&
      ["Admin", "ASD", "Authorizer", "Viewer"].includes(userData.Type) &&
      !hasPermission(userData, "commission_stmt.view")
    ) {
      navigate("/");
      window.location.reload();
    } else if (userData && userData?.Type === "Manufacturer") {
      navigate("/");
      window.location.reload();
    }
    getData()
      .then((data) => {
        setCommissionData(data);
      })
      .then(() => {
        getReferralCommissionData().then((data) => {
          setReferralCommissionData(data);
        });
      });
    
    // dummyDistributorCommissionStatements.find((el) => {
    //   if (Number(id) === el.DistributorID) {
    //     setCommDist(el);
    //   }
    // });
  }, [id]);

  useEffect(() => {
    let isMounted = true;
    const resolveStatementStatus = async () => {
      if (typeof location.state?.IsGenerated === "boolean") {
        if (isMounted) setIsStatementGenerated(location.state.IsGenerated);
        return;
      }
      if (location.state?.CommissionStatementID) {
        const generated = await fetchStatementGenerated(
          location.state.CommissionStatementID
        );
        if (isMounted) setIsStatementGenerated(generated);
        return;
      }
      if (isMounted) setIsStatementGenerated(false);
    };

    resolveStatementStatus();
    return () => {
      isMounted = false;
    };
  }, [location.state?.IsGenerated, location.state?.CommissionStatementID]);

  const getInvoiceCommissionsForExport = async (invoiceRow) => {
    const apiServe = new APIService();
    const response = await apiServe.addDataToBackend(
      {
        ...getStatementPayload(),
        InvoiceID: invoiceRow?.InvoiceID,
      },
      "getcommissionsforinvoice"
    );

    if (!response?.data) {
      throw new Error(
        `No commission data returned for invoice ${invoiceRow?.InvoiceNumber || invoiceRow?.InvoiceID}.`
      );
    }

    return response.data;
  };

  const buildOpenBalance = (invoiceMetadata, invoiceRow) => {
    if (invoiceMetadata?.OpenBalance !== null && invoiceMetadata?.OpenBalance !== undefined) {
      return toNumber(invoiceMetadata.OpenBalance);
    }

    const hasSummaryBalanceInputs =
      invoiceRow?.InvoiceTotal !== undefined &&
      invoiceRow?.ShippingCost !== undefined &&
      invoiceRow?.CommissionApplied !== undefined;

    if (hasSummaryBalanceInputs) {
      return (
        toNumber(invoiceRow.InvoiceTotal) +
        toNumber(invoiceRow.ShippingCost) -
        toNumber(invoiceRow.CommissionApplied)
      );
    }

    return 0;
  };

  const buildCommissionExportRows = async () => {
    const uniqueInvoices = Array.from(
      new Map(
        (commissionData || [])
          .filter((row) => row?.InvoiceID)
          .map((row) => [row.InvoiceID, row])
      ).values()
    );

    const invoiceResponses = await Promise.all(
      uniqueInvoices.map(async (invoiceRow) => {
        try {
          const invoiceCommissionResponse =
            await getInvoiceCommissionsForExport(invoiceRow);
          return {
            invoiceRow,
            invoiceMetadata: invoiceCommissionResponse?.invoice || {},
            tissueRows: Array.isArray(invoiceCommissionResponse?.data)
              ? invoiceCommissionResponse.data
              : [],
          };
        } catch (error) {
          console.error("Failed to fetch commission export data", error);
          throw new Error(
            `Failed to load commission data for invoice ${invoiceRow?.InvoiceNumber || invoiceRow?.InvoiceID}.`
          );
        }
      })
    );

    return invoiceResponses.flatMap(
      ({ invoiceRow, invoiceMetadata, tissueRows }) =>
        tissueRows.map((tissueRow) => ({
          TissueID: tissueRow?.TissueID || "",
          ProductCode: tissueRow?.ProductCode || "",
          Cost: toNumber(tissueRow?.Cost),
          Paid: toNumber(tissueRow?.Paid),
          CommissionRate:
            tissueRow?.DistributorCommissionPercentage ??
            tissueRow?.DistributorPercentage ??
            0,
          CommissionDue: toNumber(tissueRow?.CommissionDue),
          DateApplied: tissueRow?.DateApplied || null,
          InvoiceNumber:
            tissueRow?.InvoiceNumber ||
            invoiceMetadata?.InvoiceNumber ||
            invoiceRow?.InvoiceNumber ||
            "",
          PONumber: invoiceMetadata?.PONumber || "",
          InvoiceDate:
            tissueRow?.InvoiceDate ||
            invoiceMetadata?.InvoiceDate ||
            invoiceRow?.InvoiceDate ||
            null,
          AccountName:
            invoiceMetadata?.AccountName ||
            invoiceRow?.AccountName ||
            tissueRow?.AccountName ||
            "",
          Manufacturer:
            invoiceMetadata?.ManufacturerName ||
            invoiceRow?.ManufacturerName ||
            tissueRow?.ManufacturerName ||
            "",
          OpenBalance: buildOpenBalance(invoiceMetadata, invoiceRow),
        }))
    );
  };

  const downloadCommissionExport = async (format) => {
    setExportOption(null);

    if (format !== "CSV" && format !== "Excel") {
      return;
    }

    if (selectedSegment !== "Sales") {
      Swal.fire({
        icon: "info",
        title: "Sales commission export only",
        text: "Switch to Sales Commission to export invoice-level commission data.",
      });
      return;
    }

    if (!commissionStatementID || !distributorID) {
      Swal.fire({
        icon: "error",
        title: "Export unavailable",
        text: "This commission statement is missing the information required for export.",
      });
      return;
    }

    if (!commissionData?.length) {
      Swal.fire({
        icon: "info",
        title: "No data to export",
        text: "There are no commission records available for export.",
      });
      return;
    }

    try {
      setIsExporting(true);
      const exportRows = await buildCommissionExportRows();

      if (!exportRows.length) {
        Swal.fire({
          icon: "info",
          title: "No data to export",
          text: "There are no invoice commission line items available for export.",
        });
        return;
      }

      const fileName = `Commission Statement ${statementData?.StatementMonthYear || distributorID}`;
      if (format === "CSV") {
        exportToCSV(exportRows, fileName, COMMISSION_EXPORT_COLUMNS);
      } else {
        exportToXLSX(exportRows, fileName, COMMISSION_EXPORT_COLUMNS);
      }
    } catch (error) {
      console.error("Failed to export commission statement", error);
      Swal.fire({
        icon: "error",
        title: "Export failed",
        text:
          error?.message ||
          "The commission statement export could not be generated. Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const tableHeadersCommissionsStatementsDistributorSales = [
    {
      headerName: "Invoice",
      field: "InvoiceNumber",
      filter: true,
      cellRenderer: (params) => {
        return (
          <TableLink
            value={params.data.InvoiceNumber}
            path={`/InvoiceHome/${params.data.InvoiceID}`}
          />
        );
      },
    },
    {
      headerName: "Account",
      field: "AccountName",
      filter: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.AccountName}
          path={`/Account/${params.data.AccountID}`}
        />
      ),
    },
    {
      headerName: "Manufacturer",
      field: "ManufacturerName",
      filter: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.ManufacturerName}
          path={`/Manufacturer/${params.data.ManufacturerID}`}
        />
      ),
    },
    {
      headerName: "Invoice Date",
      field: "InvoiceDate",
      filter: true,
      cellRenderer: (params) => {
        // const value = params.data.InvoiceDate.split("T")[0];
        return `${moment(params.data.InvoiceDate).format("MM-DD-YYYY")}`;
      },
    },
    {
      headerName: "Products",
      field: "ProductsQty",
      filter: true,
    },
    {
      headerName: "Comm. Applied",
      field: "CommissionApplied",
      filter: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(
          String(params?.data?.CommissionApplied).replace(/[^0-9.-]+/g, "")
        ),
      cellRenderer: (params) => {
        const value = parseFloat(
          String(params?.data?.CommissionApplied).replace(/[^0-9.-]+/g, "")
        );
        return `$${value.toLocaleString()}`;
      },
    },
    {
      headerName: "Distributor Comm. Due",
      field: "CommissionDue",
      filter: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(
          String(params?.data?.CommissionDue).replace(/[^0-9.-]+/g, "")
        ),
      cellRenderer: (params) => {
        const value = parseFloat(
          String(params?.data?.CommissionDue).replace(/[^0-9.-]+/g, "")
        );
        return `$${value.toLocaleString()}`;
      },
    },
    {
      headerName: "Options",
      field: "",
      sortable: false,
      cellRenderer: (params) => {
        console.log("Row Data for Options:", params.data);
        return (
          <TableOptionsCell
            optionOne="View"
            optionOneType="warning"
            optionOneClickPath={`/CommissionStatementsProducts/${params.data.InvoiceID}`}
            rowData={params.data}
            moreData={{
              DistributorID: location.state?.DistributorID || id,
              CommissionStatementID:
                location.state?.CommissionStatementID || null,
            }}
            type="CommissionStatementsDistributor"
          />
        );
      },
    },
  ];

  if(user.Type === "Admin"){
    tableHeadersCommissionsStatementsDistributorSales.splice(6, 0, {
      headerName: "AS Comm. Recd",
      field: "CommissionReceived",
      filter: true,
      cellDataType: "number",
      valueGetter: (params) =>
      parseFloat(
        String(params?.data?.CommissionReceived).replace(/[^0-9.-]+/g, "")
      ),
      cellRenderer: (params) => {
      const value = parseFloat(
        String(params?.data?.CommissionReceived).replace(/[^0-9.-]+/g, "")
      );
      return `$${value.toLocaleString()}`;
      },
    });
  }

  const tableHeadersCommissionsStatementsDistributorReferral = [
    {
      headerName: "Invoice",
      field: "Invoice Number",
      filter: true,
      cellRenderer: (params) => {
        return (
          <TableLink
            value={params.data.InvoiceNumber}
            path={`/InvoiceHome/${params.data.InvoiceID}`}
          />
        );
      },
    },
    {
      headerName: "Referee Distributor",
      field: "RefereeDistributorName",
      filter: true,
    },
    {
      headerName: "Comm. Applied",
      field: "CommissionApplied",
      filter: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(
          String(params?.data?.CommissionApplied).replace(/[^0-9.-]+/g, "")
        ),
      cellRenderer: (params) => {
        const value = parseFloat(
          String(params?.data?.CommissionApplied).replace(/[^0-9.-]+/g, "")
        );
        return `$${value.toLocaleString()}`;
      },
    },
    {
      headerName: "Referral Rate",
      field: "ReferralRate",
      filter: true,
      // cellRenderer: (params) => (
      //   <TableLink
      //     value={params.data.ManufacturerName}
      //     path={`/Manufacturer/${params.data.ManufacturerID}`}
      //   />
      // ),
    },
    {
      headerName: "Referral Commission",
      field: "ReferralCommission",
      filter: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(
          String(params?.data?.ReferralCommission).replace(/[^0-9.-]+/g, "")
        ),
      cellRenderer: (params) => {
        const value = parseFloat(
          String(params?.data?.ReferralCommission).replace(/[^0-9.-]+/g, "")
        );
        return `$${value.toLocaleString()}`;
      },
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      <TitleBar
        entityName="Commission Statement by Invoice"
        // label={`Commissions for ${commissionData && commissionData.length > 0 ? commissionData[0]?.DistributorName : referralCommissionData && referralCommissionData.length > 0 ? referralCommissionData[0]?.DistributorName : ''}`}
        label={`Commissions for ${statementData?.StatementMonthYear ?? ""}`}
        filterBarComponents={
          <>
            <Button
              isActive={selectedSegment === "Sales"}
              variant="timeframe"
              onClick={() => handleSegmentChange("Sales")}
            >
              Sales Commission
            </Button>
            <Button
              isActive={selectedSegment === "Referral"}
              variant="timeframe"
              onClick={() => handleSegmentChange("Referral")}
            >
              Referral Commissions
            </Button>
          </>
        }
        rightFilterBarComponents={
          <DropdownComponent
            label="Export"
            placeholder={isExporting ? "Exporting..." : "Select Format"}
            options={[
              { label: "Excel", value: "Excel" },
              { label: "CSV", value: "CSV" },
            ]}
            value={exportOption}
            setValue={setExportOption}
            parentFunc={downloadCommissionExport}
            type="ExcelExport"
            isDisabled={
              isExporting ||
              selectedSegment !== "Sales" ||
              !commissionData?.length
            }
          />
        }
      />
      {selectedSegment === "Sales" && (
        <Table
          rowData={commissionData}
          columnDefs={tableHeadersCommissionsStatementsDistributorSales}
        />
      )}
      {selectedSegment === "Referral" && (
        <Table
          rowData={referralCommissionData}
          columnDefs={tableHeadersCommissionsStatementsDistributorReferral}
        />
      )}
    </div>
  );
};

export default CommissionStatementsDistributor;
